import { createNodesChunkedProductsService } from '@theme/storefront-api';
import { PRODUCT_TILE_NODES_QUERY } from '@theme/product-queries';
import { getThemeTranslation } from '@theme/utilities';
import {
  applyProductCardImage,
  buildProductCardStateForVariant,
  chooseFirstAvailableVariant,
  computeCardBadge,
  enrichIfMinimal,
  getVariantOptionValue,
  formatCardPrice,
  imageUrlWithSize,
  isExplodedCollectionCard,
  normalizeVariantName,
  parseCardPriceValue,
  getFilteredSwatches,
  readExplodedSwatchContext,
} from '@theme/product-card-helpers';
import {
  getDiscountedPrices,
  isRegiosDiscount,
  getRegiosDiscountTitle,
  buildRegiosCardInputs,
} from '@theme/regios-pricing';

/**
 * @typedef {import('@theme/product-card-helpers').ProductCardData} ProductCardData
 * @typedef {import('@theme/product-card-helpers').ProductCardSeedData} ProductCardSeedData
 * @typedef {import('@theme/product-card-helpers').ProductSwatch} ProductSwatch
 *
 * @typedef {object} ProductCardViewModelData
 * @property {HTMLElement|null} $link
 * @property {HTMLElement|null} $imageWrapper
 * @property {HTMLElement|null} $title
 * @property {HTMLElement|null} $price
 * @property {HTMLElement|null} $badgeLabel
 * @property {HTMLElement|null} $secondaryBadgeCopy
 * @property {HTMLElement|null} $outOfStock
 * @property {HTMLElement|null} $customizableCallout
 * @property {HTMLElement|null} $swatchList
 * @property {HTMLElement|null} $swatchTemplate
 * @property {HTMLElement|null} $infoFooter
 */

const { fetchProductsBatched } = createNodesChunkedProductsService({
  query: PRODUCT_TILE_NODES_QUERY,
  chunkSize: 25,
  flushDelay: 5,
  apiVersion: '2026-04',
});

/**
 * Search bundles announce the first product ids as soon as Algolia responds,
 * so GraphQL starts before the cards mount. Cards join the in-flight request.
 * Must match PRODUCT_CARD_PREFETCH_EVENT in src/search-tool/lib/prefetchProductCards.ts.
 */
const PRODUCT_CARD_PREFETCH_EVENT = 'product-card:prefetch';

/** @param {unknown} ids */
function prefetchProductCards(ids) {
  if (!Array.isArray(ids) || ids.length === 0) return;
  fetchProductsBatched(ids).catch(() => {});
}

document.addEventListener(PRODUCT_CARD_PREFETCH_EVENT, (event) => {
  prefetchProductCards(
    /** @type {CustomEvent<{ productIds?: unknown }>} */ (event).detail?.productIds
  );
});

const PRODUCT_SELECTORS = {
  LINK: '.image-button__link, .c-product-tile-decorator__content-wrapper',
  IMAGE_WRAPPER: '.c-product-tile__image-wrapper',
  TITLE: '[data-replaceable-card-title]',
  PRICE: '[data-replaceable-card-price]',
  BADGE_LABEL: '[data-replaceable-card-badge-label]',
  SECONDARY_BADGE_COPY: '[data-replaceable-card-secondary-badge-copy]',
  OUT_OF_STOCK: '[data-replaceable-card-out-of-stock]',
  CUSTOMIZABLE_CALLOUT: '[data-replaceable-card-customizable-callout]',
  SWATCH_LIST: '.c-product-option__list',
  SWATCH_TEMPLATE: '.c-product-option__list .o-style-option__list-item',
  SWATCH_CLASS: 'color-selector',
  INFO_FOOTER_CLASS: 'c-product-tile__footer',
};

/**
 * Visible swatch counts by viewport. Breakpoints match the theme scale:
 * Mobile <750 | Tablet/small desktop 750–1199 | Large desktop 1200–1399 | Design desktop ≥1400.
 *
 * @type {ReadonlyArray<{ viewport: 'mobile'|'tablet'|'desktop-sm'|'desktop', count: number }>}
 */
const SWATCH_OVERFLOW_STEPS = [
  { viewport: 'mobile', count: 3 },
  { viewport: 'tablet', count: 4 },
  { viewport: 'desktop-sm', count: 5 },
  { viewport: 'desktop', count: 6 },
];

const DESKTOP_VISIBLE_SWATCH_COUNT = 6;

/**
 * @param {string|null|undefined} value
 * @returns {string}
 */
function normalizeAttributeValue(value) {
  return String(value || '').trim();
}

/** @type {WeakMap<HTMLElement, IntersectionObserver>} */
const enrichObservers = new WeakMap();

/**
 * GraphQL enrichment waits until the card is near the viewport unless it is
 * the prioritized LCP card (data-priority-image).
 *
 * @param {HTMLElement} element
 * @returns {Promise<void>}
 */
/** @type {Promise<typeof import('@theme/product-card-image-carousel')>|undefined} */
let productCardImageCarouselModulePromise;

/** @returns {Promise<typeof import('@theme/product-card-image-carousel')>} */
function loadProductCardImageCarouselModule() {
  productCardImageCarouselModulePromise ??= import(
    '@theme/product-card-image-carousel'
  );
  return productCardImageCarouselModulePromise;
}

/** @type {Promise<typeof import('@theme/product-card-quick-shop')>|undefined} */
let productCardQuickShopModulePromise;

/** @returns {Promise<typeof import('@theme/product-card-quick-shop')>} */
function loadProductCardQuickShopModule() {
  productCardQuickShopModulePromise ??= import('@theme/product-card-quick-shop');
  return productCardQuickShopModulePromise;
}

const SKELETON_PLACEHOLDER_SRC =
  /no-image-2048|placeholder\.gif|\/assets\/no-image/i;

/**
 * Removes Shopify skeleton placeholder GIFs only. Does not hide the well
 * (no blanket is-loading) so a late data-image attribute can still paint.
 *
 * @param {HTMLElement} root
 */
function stripSkeletonPlaceholderImages(root) {
  root
    .querySelectorAll(
      '.product-card-carousel__image, .product-card-carousel__base-image'
    )
    .forEach((node) => {
      if (!(node instanceof HTMLImageElement)) return;
      const src = node.getAttribute('src') || '';
      if (!SKELETON_PLACEHOLDER_SRC.test(src)) return;
      node.removeAttribute('src');
      node.removeAttribute('srcset');
      node.removeAttribute('sizes');
    });
}

/**
 * Applies Algolia seed attrs to the visible slide when present.
 *
 * @param {HTMLElement} root
 * @returns {boolean}
 */
function paintSeedImageFromAttributes(root) {
  const seed = normalizeAttributeValue(root.getAttribute('data-image'));
  if (!seed) return false;

  const title = normalizeAttributeValue(root.getAttribute('data-title'));
  const prioritize = root.hasAttribute('data-priority-image');
  const slideImage = root.querySelector('.product-card-carousel__image');

  if (!(slideImage instanceof HTMLImageElement)) return false;

  applyProductCardImage(
    slideImage,
    { src: seed, alt: title },
    title,
    { prioritize }
  );
  return true;
}

function waitForEnrichIfDeferred(element) {
  if (element.hasAttribute('data-priority-image')) {
    return Promise.resolve();
  }

  if (!('IntersectionObserver' in window)) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const existing = enrichObservers.get(element);
    existing?.disconnect();

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        enrichObservers.delete(element);
        resolve();
      },
      { rootMargin: '250px 0px' }
    );

    enrichObservers.set(element, observer);
    observer.observe(element);
  });
}

/**
 * US market detection for Doogma PLP callout.
 * Prefer the URL locale segment (`/en-gb/`, `/en-us/`) over `Shopify.country`,
 * which can lag behind the path when browsing EMEA markets.
 * @returns {boolean}
 */
function isUsMarket() {
  const pathSegment =
    String(window.location?.pathname || '')
      .split('/')
      .find(Boolean) || '';
  const themeLocale = String(
    typeof Theme !== 'undefined' ? Theme?.localization?.locale || '' : ''
  ).toLowerCase();
  const locale = /^[a-z]{2}-[a-z]{2}$/i.test(pathSegment)
    ? pathSegment.toLowerCase()
    : /^[a-z]{2}-[a-z]{2}$/i.test(themeLocale)
      ? themeLocale
      : '';

  if (locale) {
    return locale.endsWith('-us');
  }

  return String(window.Shopify?.country || '').toUpperCase() === 'US';
}

/**
 * @param {ParentNode} parent
 * @param {string} selector
 * @returns {HTMLElement|null}
 */
function queryHTMLElement(parent, selector) {
  const element = parent.querySelector(selector);
  return element instanceof HTMLElement ? element : null;
}

class ProductCardViewModel {
  /** @param {HTMLElement} scope */
  constructor(scope) {
    this.$scope = scope;
    /** @type {ProductCardViewModelData} */
    this.vm = this.getViewModel();
  }

  /** @returns {ProductCardViewModelData} */
  getViewModel() {
    return {
      $link: queryHTMLElement(this.$scope, PRODUCT_SELECTORS.LINK),
      $imageWrapper: queryHTMLElement(
        this.$scope,
        PRODUCT_SELECTORS.IMAGE_WRAPPER
      ),
      $title: queryHTMLElement(this.$scope, PRODUCT_SELECTORS.TITLE),
      $price: queryHTMLElement(this.$scope, PRODUCT_SELECTORS.PRICE),
      $badgeLabel: queryHTMLElement(this.$scope, PRODUCT_SELECTORS.BADGE_LABEL),
      $secondaryBadgeCopy: queryHTMLElement(
        this.$scope,
        PRODUCT_SELECTORS.SECONDARY_BADGE_COPY
      ),
      $outOfStock: queryHTMLElement(this.$scope, PRODUCT_SELECTORS.OUT_OF_STOCK),
      $customizableCallout: queryHTMLElement(
        this.$scope,
        PRODUCT_SELECTORS.CUSTOMIZABLE_CALLOUT
      ),
      $swatchList: queryHTMLElement(this.$scope, PRODUCT_SELECTORS.SWATCH_LIST),
      $swatchTemplate: queryHTMLElement(
        this.$scope,
        PRODUCT_SELECTORS.SWATCH_TEMPLATE
      ),
      $infoFooter: queryHTMLElement(
        this.$scope,
        `.${PRODUCT_SELECTORS.INFO_FOOTER_CLASS}`
      ),
    };
  }
}

class ProductCardDOMPatcher {
  /** 
   * @param {ProductCardComponent} scope
   * @param {boolean} isMinimal
   */
  constructor(scope, isMinimal) {
    this.scope = scope;
    this.isMinimal = isMinimal
    /** @type {ProductCardViewModelData} */
    this.viewModel = /** @type {ProductCardViewModelData} */ (
      scope.viewModel?.vm
    );
  }

  /** @param {ProductCardData} data */
  render(data) {
    this.setLink(data);
    this.renderTitle(data);
    this.renderOutOfStock(data);
    this.renderImage(data);
    this.renderPrice(data);
    this.renderBadgeLabel(data);
    this.renderSecondaryBadgeCopy(data);
    this.renderCustomizableCallout(data);
  }

  /** @param {ProductCardData} data */
  setLink(data) {
    const { $link } = this.viewModel;
    if ($link && data.url) $link.setAttribute('href', data.url);
    if ($link && data.title) $link.setAttribute('aria-label', data.title);
  }

  /** @param {ProductCardData} data */
  renderImage(data) {
    this.scope.imageCarousel?.render(data);
  }

  /** @param {ProductCardData} data */
  renderTitle(data) {
    const { $title } = this.viewModel;
    if ($title && data.title) $title.textContent = data.title;
  }

  /**
   * Renders the card price.
   * @param {ProductCardData} data
   */
  renderPrice(data) {
    const { $price } = this.viewModel;
    const hasPrice = data.price != null && data.price !== '';
    if (!$price || (!hasPrice && !data.regiosPrice)) return;

    let current = '';
    let compare = '';

    if (data.regiosPrice) {
      current = data.regiosPrice.current;
      compare = data.regiosPrice.compare;
    } else {
      current = formatCardPrice(data.price);
      const mainValue = parseCardPriceValue(data.price);
      const compareAtValue = parseCardPriceValue(data.compareAtPrice);
      const hasCompareAt =
        compareAtValue != null &&
        compareAtValue > 0 &&
        (mainValue == null || compareAtValue > mainValue);
      compare = hasCompareAt ? formatCardPrice(data.compareAtPrice) : '';
    }

    const wrapper = document.createElement('div');
    wrapper.className = 'c-product-price c-product-tile__product-price';

    const currentEl = document.createElement('span');
    currentEl.className = compare
      ? 'c-product-price__formatted-price body-sm c-product-price__formatted-price--red'
      : 'c-product-price__formatted-price body-sm';
    currentEl.textContent = current;
    wrapper.appendChild(currentEl);

    if (compare) {
      const compareEl = document.createElement('span');
      compareEl.className = 'c-product-price__formatted-price--discounted body-sm';
      compareEl.textContent = compare;
      wrapper.appendChild(compareEl);
    }

    $price.replaceChildren(wrapper);
  }

  /** @param {ProductCardData} data */
  renderBadgeLabel(data) {
    const { $badgeLabel } = this.viewModel;
    if (!$badgeLabel) return;

    const badgeData = data.productBadge;
    const badgeText =
      badgeData?.copy ||
      (badgeData?.copyKey && getThemeTranslation(badgeData?.copyKey)) ||
      null;

    if (!badgeText) {
      $badgeLabel.innerHTML = '';
      return;
    }

    const badge = document.createElement('span');
    const classNames = 'c-product-tile__badge-label body-sm'
    badge.className = badgeData?.isPromo ? `${classNames} c-product-tile__badge-label--promo` : classNames;
    badge.textContent = badgeText;
    $badgeLabel.replaceChildren(badge);
  }

  /** @param {ProductCardData} data */
  renderSecondaryBadgeCopy(data) {
    const { $secondaryBadgeCopy } = this.viewModel;
    if (!$secondaryBadgeCopy || this.isMinimal) return;

    const text = String(data.flags?.secondaryBadgeCopy || '').trim();
    $secondaryBadgeCopy.textContent = text;
    $secondaryBadgeCopy.toggleAttribute('hidden', !text);
  }

  /** @param {ProductCardData} data */
  renderOutOfStock(data) {
    const { $outOfStock, $price } = this.viewModel;
    if (!$outOfStock) return;

    const isDiscontinued =
      String(data.flags?.outOfStockState || '').trim().toLowerCase() ===
      'discontinued';
    const isOutOfStock = !!data.allVariantsOutOfStock && isDiscontinued;

    if (!this.isMinimal) {
      $outOfStock.textContent = isOutOfStock
        ? getThemeTranslation('product_card_out_of_stock')
        : '';
      $outOfStock.toggleAttribute('hidden', !isOutOfStock);
    }

    if ($price) $price.toggleAttribute('hidden', isOutOfStock);
  }

  /**
   * Shows the Customizable callout on product cards only (not PDP) when:
   * - Doogma (`custom.doogma_template_id`): US market only. EMEA must not
   *   show this callout even if a deep link also falls back from primary.
   * - Rogue (deep link only, no Doogma template): when
   *   `custom.custom_deep_link` is populated for the current market.
   * @param {ProductCardData} data
   */
  renderCustomizableCallout(data) {
    const { $customizableCallout } = this.viewModel;
    if (!$customizableCallout || this.isMinimal) return;

    const customDeepLink = String(data.flags?.customDeepLink || '').trim();
    const doogmaTemplateId = String(data.flags?.doogmaTemplateId || '').trim();
    const isCustomizable = doogmaTemplateId
      ? isUsMarket()
      : Boolean(customDeepLink);

    $customizableCallout.toggleAttribute('hidden', !isCustomizable);
  }

  /** @param {ProductSwatch[]} swatches */
  renderSwatches(swatches) {
    const { $swatchList, $swatchTemplate } = this.viewModel;
    if (!$swatchList || !$swatchTemplate || this.isMinimal) return;

    const exploded = isExplodedCollectionCard(this.scope);
    const multiSwatch =
      exploded && readExplodedSwatchContext(this.scope).multiSwatch;
    const filtered = multiSwatch
      ? {
          items: (swatches || []).filter(Boolean),
          shouldRenderSwatches: (swatches || []).filter(Boolean).length > 0,
        }
      : getFilteredSwatches(swatches, exploded);
    const { items, shouldRenderSwatches } = filtered;

    $swatchList.innerHTML = '';
    $swatchList.toggleAttribute('hidden', !shouldRenderSwatches);

    if (!shouldRenderSwatches) return;

    const selectedIndex = items.findIndex((swatch) => swatch?.isChecked);
    const selectedSwatch = selectedIndex > 0 ? items[selectedIndex] : null;
    const orderedItems = selectedSwatch
      ? [
          selectedSwatch,
          ...items.slice(0, selectedIndex),
          ...items.slice(selectedIndex + 1),
        ]
      : items;

    const visibleSwatches = orderedItems.slice(0, DESKTOP_VISIBLE_SWATCH_COUNT);

    for (const [index, swatch] of visibleSwatches.entries()) {
      if (!swatch) continue;

      const clone = /** @type {HTMLElement} */ (
        $swatchTemplate.cloneNode(true)
      );

      for (const step of SWATCH_OVERFLOW_STEPS) {
        if (step.count >= DESKTOP_VISIBLE_SWATCH_COUNT) continue;
        clone.classList.toggle(
          `product-card-swatch--${step.viewport}-extra`,
          index >= step.count
        );
      }

      const button =
        /** @type {HTMLElement|null} */ (
          clone.querySelector(`.${PRODUCT_SELECTORS.SWATCH_CLASS}`)
        ) || clone.querySelector('button, a');
      const img = clone.querySelector('img');

      if (button) {
        button.classList.toggle('is-checked', Boolean(swatch.isChecked));
        button.classList.toggle('is-unavailable', !swatch.isAvailable);
        button.setAttribute(
          'data-product-id',
          String(this.scope.productData?.id || '')
        );
        button.setAttribute('data-option-name', swatch.optionName);
        button.setAttribute('data-swatch-value', swatch.value);
        button.setAttribute('data-color-label', swatch.label);
        button.setAttribute('data-swatch-image', swatch.image?.src || '');
        button.setAttribute('data-variant-id', swatch.variantId || '');
        button.setAttribute('aria-label', swatch.label);
        button.removeAttribute('disabled');
      }

      if (img && swatch.image?.src) {
        const swatchSrc = swatch.image.src;
        img.src = imageUrlWithSize(swatchSrc, 48, 48);
        img.srcset = [32, 48, 64, 80]
          .map((size) => `${imageUrlWithSize(swatchSrc, size, size)} ${size}w`)
          .join(', ');
        img.sizes = '(min-width: 968px) 2.5rem, 2rem';
        img.alt = swatch.label;
        img.loading = 'lazy';
      }

      $swatchList.appendChild(clone);

      for (const step of SWATCH_OVERFLOW_STEPS) {
        if (index + 1 !== step.count) continue;
        this.appendSwatchMoreIndicator(
          $swatchList,
          orderedItems.length - step.count,
          step.viewport
        );
      }
    }
  }

  /**
   * @param {HTMLElement} list
   * @param {number} count
   * @param {'mobile'|'tablet'|'desktop-sm'|'desktop'} viewport
   */
  appendSwatchMoreIndicator(list, count, viewport) {
    if (count <= 0) return;

    const item = document.createElement('li');
    const moreText = getThemeTranslation('product_card_swatch_more');
    const hiddenLabel = getThemeTranslation(
      'product_card_more_options',
      { count }
    );
    item.className = `product-card-swatch-more product-card-swatch-more--${viewport}`;
    if (hiddenLabel) item.setAttribute('aria-label', hiddenLabel);

    const label = document.createElement('span');
    label.className = 'body-sm';
    label.setAttribute('aria-hidden', 'true');
    label.append(`+${count}`);
    label.appendChild(document.createElement('br'));
    label.append(moreText);
    item.appendChild(label);
    list.appendChild(item);
  }

  hideSwatches() {
    this.viewModel.$swatchList?.setAttribute('hidden', '');
  }

  /**
   * Paints title + price from the instant Algolia seed and reveals the footer,
   * so they show on first paint instead of waiting for GraphQL enrichment.
   * @param {{ title?: string|null, price?: string|number|null, compareAtPrice?: string|number|null }} seed
   */
  renderSeed(seed) {
    let painted = false;
    if (seed.title) {
      this.renderTitle(/** @type {ProductCardData} */ (seed));
      painted = true;
    }

    if (seed.price != null && seed.price !== '') {
      this.renderPrice(/** @type {ProductCardData} */ (seed));
      this.viewModel.$price?.classList.remove('regios-pending');
      painted = true;
    }
    if (painted) this.revealInfoFooter();
  }

  revealInfoFooter() {
    this.viewModel.$infoFooter?.classList.remove(
      'c-product-tile__footer--loading'
    );
  }

  initialCardBuildCleanUp() {
    this.revealInfoFooter();
  }
}

class VariantSwatchController {
  /**
   * @param {{
   *   root: ProductCardComponent,
   *   viewModel: ProductCardViewModel,
   *   data: ProductCardData,
   *   swatchOptionName: string,
   *   onVariantSelect?: (productData: ProductCardData) => void
   *   isMinimal?: boolean
   * }} opts
   */
  constructor({ root, viewModel, data, swatchOptionName, onVariantSelect, isMinimal }) {
    this.root = root;
    this.viewModel = viewModel.vm;
    this.productData = data;
    this.swatchOptionName = swatchOptionName;
    this.onVariantSelect = onVariantSelect;
    this.isMinimal = isMinimal;
    this._swatchButtons = null;
    this.handleSwatchClickBound = this.handleSwatchClick.bind(this);
  }

  initialize() {
    const exploded = isExplodedCollectionCard(this.root);

    if (
      !this.swatchOptionName ||
      (!exploded && (this.productData.swatches?.length || 0) <= 1) ||
      this.isMinimal
    ) {
      this.root.domPatcher?.hideSwatches();
      return;
    }

    this.root.domPatcher?.renderSwatches(this.productData.swatches);
    this._swatchButtons = null;
    this.attachSwatchListeners();
  }

  /** @param {MouseEvent} event */
  handleSwatchClick(event) {
    event.preventDefault();
    if (!(event.currentTarget instanceof HTMLElement)) return;

    const swatch = event.currentTarget;
    const value = swatch.getAttribute('data-swatch-value');
    if (!value || swatch.classList.contains('is-checked')) return;

    const variantId = swatch.getAttribute('data-variant-id');
    const swatchVariant = variantId
      ? this.productData.variants.find((variant) => variant.id === variantId)
      : null;
    const selectedVariant = swatchVariant || this.selectVariantForSwatch(value);
    if (!selectedVariant) return;

    this.updateSwatchSelection(swatch);
    this.productData = buildProductCardStateForVariant(
      this.productData,
      selectedVariant,
      this.swatchOptionName,
      readExplodedSwatchContext(this.root)
    );
    this.onVariantSelect?.(this.productData);
  }

  /** @param {string} value */
  selectVariantForSwatch(value) {
    const valueKey = normalizeVariantName(value);
    const matchingVariants = this.productData.variants.filter(
      (variant) =>
        normalizeVariantName(
          getVariantOptionValue(variant, this.swatchOptionName)
        ) === valueKey
    );

    return chooseFirstAvailableVariant(matchingVariants);
  }

  /** @param {HTMLElement} currentSwatch */
  updateSwatchSelection(currentSwatch) {
    this.swatchButtons.forEach((btn) => btn.classList.remove('is-checked'));
    currentSwatch.classList.add('is-checked');
  }

  attachSwatchListeners() {
    this.swatchButtons.forEach((swatch) => {
      swatch.addEventListener('click', this.handleSwatchClickBound);
    });
  }

  detachSwatchListeners() {
    this.swatchButtons.forEach((swatch) => {
      swatch.removeEventListener('click', this.handleSwatchClickBound);
    });
  }

  get swatchButtons() {
    if (!this._swatchButtons) {
      this._swatchButtons = /** @type {NodeListOf<HTMLElement>} */ (
        this.viewModel.$swatchList?.querySelectorAll(
          `.${PRODUCT_SELECTORS.SWATCH_CLASS}`
        ) || []
      );
    }
    return this._swatchButtons;
  }
}

class ProductCardComponent extends HTMLElement {
  static observedAttributes = ['data-image'];

  constructor() {
    super();
    /** @type {ProductCardSeedData|ProductCardData|undefined} */
    this._productData = undefined;
    /** @type {ProductCardViewModel|undefined} */
    this.viewModel = undefined;
    /** @type {ProductCardDOMPatcher|undefined} */
    this.domPatcher = undefined;
    /** @type {VariantSwatchController|undefined} */
    this.swatchController = undefined;
    /** @type {import('@theme/product-card-quick-shop').ProductCardQuickShopController|undefined} */
    this.quickShopController = undefined;
    /** @type {import('@theme/product-card-image-carousel').ProductCardImageCarousel|undefined} */
    this.imageCarousel = undefined;
    /** Guards against stale Regios results after a fast variant switch. */
    this._regiosToken = 0;
  }

  connectedCallback() {
    if (!this.querySelector('.c-product-tile')) {
      const skeleton = document.querySelector('#product-card-placeholder');
      if (skeleton) this.innerHTML = skeleton.innerHTML;
    }

    stripSkeletonPlaceholderImages(this);
    paintSeedImageFromAttributes(this);

    if (this._productData) {
      this.setup(this._productData);
      return;
    }

    const productId = normalizeAttributeValue(
      this.getAttribute('data-product-id')
    );
    if (productId) {
      this.productData = {
        id: productId,
        selectedVariantId: this.getSelectedVariantId(),
      };
    }
  }

  /**
   * React can set data-image after connectedCallback on the first tile; paint
   * as soon as the attribute lands.
   *
   * @param {string} name
   * @param {string|null} _oldValue
   * @param {string|null} newValue
   */
  attributeChangedCallback(name, _oldValue, newValue) {
    if (name !== 'data-image' || !newValue?.trim()) return;
    if (!this.querySelector('.c-product-tile')) return;

    stripSkeletonPlaceholderImages(this);
    paintSeedImageFromAttributes(this);
    this.imageCarousel?.renderSeedImage(newValue);
  }

  /** @param {ProductCardSeedData|ProductCardData} data */
  set productData(data) {
    this._productData = data;
    this.setup(data);
  }

  /** @returns {ProductCardSeedData|ProductCardData} */
  get productData() {
    return /** @type {ProductCardSeedData|ProductCardData} */ (
      this._productData
    );
  }

  getSwatchOptionName() {
    const data = /** @type {Partial<ProductCardData & ProductCardSeedData>|undefined} */ (
      this._productData
    );

    return normalizeAttributeValue(
      this.getAttribute('data-swatch-option-name') ||
        data?.swatchOptionName
    );
  }

  getSelectedVariantId() {
    const data = /** @type {Partial<ProductCardData & ProductCardSeedData>|undefined} */ (
      this._productData
    );

    return normalizeAttributeValue(
      this.getAttribute('data-selected-variant-id') ||
        data?.selectedVariantId ||
        data?.selectedVariant?.id
    );
  }

  shouldEnrich() {
    return this.getAttribute('data-enrich') === 'true';
  }

  isMinimal() {
    return this.getAttribute('data-minimal') === 'true';
  }

  /** @param {ProductCardSeedData|ProductCardData} data */
  async setup(data) {
    const isMinimal = this.isMinimal();
    this.swatchController?.detachSwatchListeners();
    this.quickShopController?.destroy();
    this.imageCarousel?.destroy();

    this.viewModel = new ProductCardViewModel(this);
    this.domPatcher = new ProductCardDOMPatcher(this, isMinimal);
    const swatchOptionName = this.getSwatchOptionName();

    stripSkeletonPlaceholderImages(this);
    paintSeedImageFromAttributes(this);

    const carouselModulePromise = loadProductCardImageCarouselModule();

    // Seed data (Algolia) paints immediately; only the GraphQL enrichment
    // below waits for the viewport. Seed images stay lazy off-screen.
    this.domPatcher.renderSeed({
      title: this.getAttribute('data-title'),
      price: this.getAttribute('data-price'),
      compareAtPrice: this.getAttribute('data-compare-at-price'),
    });

    const { ProductCardImageCarousel } = await carouselModulePromise;
    this.imageCarousel = new ProductCardImageCarousel({
      root: this,
      viewModel: this.viewModel.vm,
      swatchOptionName,
      isMinimal,
    });

    this.imageCarousel.renderSeedImage(this.getAttribute('data-image'));

    if (!this.hasAttribute('data-priority-image')) {
      await waitForEnrichIfDeferred(this);
    }

    const selectedVariantId = this.getSelectedVariantId();
    const enriched = /** @type {ProductCardData} */ (
      await enrichIfMinimal(data, {
        enrich: this.shouldEnrich(),
        fetchProductsBatched,
        selectedVariantId,
        swatchOptionName,
        swatchContext: readExplodedSwatchContext(this),
      })
    );

    this._productData = enriched;

    const resolvedSwatchOptionName =
      enriched?.swatchOptionName || swatchOptionName;

    if (this.imageCarousel) {
      this.imageCarousel.swatchOptionName = resolvedSwatchOptionName;
    }

    this.swatchController = new VariantSwatchController({
      root: this,
      viewModel: this.viewModel,
      data: enriched,
      swatchOptionName: resolvedSwatchOptionName,
      isMinimal,
      onVariantSelect: (productData) => {
        this._productData = productData;
        this.domPatcher?.render(productData);
        this.quickShopController?.update(productData);
        this.applyRegios(productData);
      },
    });

    if (!isMinimal) {
      const { ProductCardQuickShopController } =
        await loadProductCardQuickShopModule();
      this.quickShopController = new ProductCardQuickShopController({
        root: this,
        viewModel: this.viewModel.vm,
        data: enriched,
      });
    }

    this.domPatcher.render(enriched);
    this.domPatcher.initialCardBuildCleanUp();
    this.swatchController.initialize();
    this.quickShopController?.initialize();

    this.applyRegios(enriched);
  }

  /**
   * @param {ProductCardData} productData
   */
  async applyRegios(productData) {
    const $price = this.viewModel?.vm?.$price;
    const token = ++this._regiosToken;
    const reveal = () => $price?.classList.remove('regios-pending');

    const inputs = buildRegiosCardInputs(productData);
    if (!inputs) {
      reveal();
      return;
    }

    const result = await getDiscountedPrices(inputs);

    // A newer variant selection happened while awaiting - discard this result rather than
    // clobbering the badge/price that a later, faster-resolving call already rendered.
    if (token !== this._regiosToken || productData !== this._productData) return;

    try {
      if (result && isRegiosDiscount(result)) {
        productData.regiosPrice = {
          current: result.salePrice.formatted,
          compare: result.regularPrice.formatted,
        };
        productData.flags.regiosDiscountActive = true;
        productData.flags.regiosDiscountTitle = getRegiosDiscountTitle(result);
      } else {
        productData.regiosPrice = null;
        productData.flags.regiosDiscountActive = false;
        productData.flags.regiosDiscountTitle = null;
      }
      // Single price renderer handles both native and Regios display.
      this.domPatcher?.renderPrice(productData);
      productData.productBadge = computeCardBadge(productData);
      this.domPatcher?.renderBadgeLabel(productData);
    } finally {
      reveal();
    }
  }

  disconnectedCallback() {
    enrichObservers.get(this)?.disconnect();
    enrichObservers.delete(this);
    this.swatchController?.detachSwatchListeners();
    this.quickShopController?.destroy();
    this.imageCarousel?.destroy();
  }
}

if (!customElements.get('product-card-custom')) {
  customElements.define('product-card-custom', ProductCardComponent);
}
